import {
  useEnabledFeatures,
  useOwnerPortalContact,
  useTeam,
  useIsOwnerPortalPreview,
} from '@vrplatform/data';
import {
  CalendarDatesIcon,
  CalendarEventIcon,
  ClipboardTextIcon,
  DashboardIcon,
  CreditCardIncomeIcon,
  FileInvoiceIcon,
  FolderOpenIcon,
  GearIcon,
  StarIcon,
  UsersIcon,
} from '@vrplatform/icons';
import { day } from '@vrplatform/utils';
import { Skeleton } from '@mantine/core';
import { NavbarRoute } from '../layout/navbar';
import { NavigationSection } from './NavigationSection';
import { useLocation } from 'react-router';
import {
  canAccessOwnerPortalGeneralSettings,
  canAccessOwnerPortalPayments,
  canAccessOwnerPortalSettings,
  canShowOwnerPortalDashboard,
  canShowOwnerPortalReservations,
} from '../routes/dashboard/OwnerRoutes.utils';
import { useOwnerPortalSelectedContact } from '../routes/dashboard/useOwnerPortalSelectedContact';

const ICON_SIZE = 18;

export const OwnerNavigation = () => {
  const [{ isGL: GL, settings }] = useTeam();
  const { ACH } = useEnabledFeatures();

  const { hasAdminAccess } = useOwnerPortalContact();

  const location = useLocation();

  const isUserPreview = useIsOwnerPortalPreview();
  const canAccessSettings = canAccessOwnerPortalSettings({
    isGL: GL,
    isUserPreview,
  });
  const canAccessGeneralSettings = canAccessOwnerPortalGeneralSettings({
    hasAdminAccess,
    isGL: GL,
    isUserPreview,
  });

  const { data: contact, isLoading } = useOwnerPortalSelectedContact();
  const isArchived = contact?.status === 'inactive';

  // Only a confirmed archive hides entries; while the archive check is loading
  // or failed, the owner routes show that state themselves.
  const canAccessPayments = canAccessOwnerPortalPayments({
    isAchEnabled: ACH,
    isGL: GL,
    isOwnerArchived: isArchived,
  });
  const canShowReservations = canShowOwnerPortalReservations({
    hostname: window.location.hostname,
    isDev: import.meta.env.DEV,
    showReservations: settings.showReservations && !isArchived,
  });
  const canShowDashboard = canShowOwnerPortalDashboard({
    isDashboardEnabled: settings.showOwnerDashboard,
    isGL: GL,
    isOwnerArchived: isArchived,
  });

  return (
    <>
      <NavigationSection>
        {canShowDashboard && (
          <NavbarRoute
            title="Dashboard"
            link="/"
            icon={<DashboardIcon size={ICON_SIZE} />}
            end
          />
        )}

        <NavbarRoute
          title="Statements"
          link="/statements"
          icon={<ClipboardTextIcon size={ICON_SIZE} />}
          end
          active={
            location.pathname.startsWith('/statements/owner-statements') ||
            location.pathname.startsWith('/statement/')
          }
        />

        {canAccessPayments && (
          <NavbarRoute
            title="Payments"
            link="/payments"
            icon={<CreditCardIncomeIcon size={ICON_SIZE} />}
          />
        )}

        <NavbarRoute
          title="Summary"
          link={`/statements/summary/${day().startOf('year').yyyymmdd()}...${day().endOf('year').yyyymmdd()}`}
          icon={<FileInvoiceIcon size={ICON_SIZE} />}
        />
      </NavigationSection>

      {isLoading ? (
        <>
          <Skeleton h={30} opacity={0.1} />
          <Skeleton h={30} opacity={0.1} />
        </>
      ) : (
        !isArchived && (
          <>
            <NavigationSection>
              {canShowReservations && (
                <>
                  <NavbarRoute
                    title="Calendar"
                    link="/calendar"
                    icon={<CalendarDatesIcon size={ICON_SIZE} />}
                  />
                  <NavbarRoute
                    title="Reservations"
                    link="/reservations"
                    icon={<CalendarEventIcon size={ICON_SIZE} />}
                  />
                </>
              )}
              {settings.showGuestReviews && (
                <NavbarRoute
                  title="Reviews"
                  link="/reviews"
                  icon={<StarIcon size={ICON_SIZE} />}
                />
              )}
            </NavigationSection>

            <NavigationSection>
              <NavbarRoute
                title="Files"
                link="/files"
                icon={<FolderOpenIcon size={ICON_SIZE} />}
              />
              {hasAdminAccess && (
                <NavbarRoute
                  title="Access"
                  link="/access"
                  icon={<UsersIcon size={ICON_SIZE} />}
                />
              )}
              {canAccessSettings && (
                <NavbarRoute
                  title="Settings"
                  link={
                    canAccessGeneralSettings
                      ? '/settings/details'
                      : '/settings/notifications'
                  }
                  icon={<GearIcon size={ICON_SIZE} />}
                />
              )}
            </NavigationSection>
          </>
        )
      )}
    </>
  );
};
